import React, { useEffect, useState, useMemo } from 'react';
import Pagination from '../components/Pagination';
import API from '../api';
import exampleCsv from '../assets/Sample_Contact_CSV.csv';
import './contacts.css';

const AVATAR_COLORS = [
  '#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981',
  '#3b82f6','#14b8a6','#f97316','#06b6d4','#84cc16',
];

function getAvatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  // add/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });

  // csv modal
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const role = localStorage.getItem('role');
  const businessId = localStorage.getItem('id');
  const isAdmin = role === 'admin';

  useEffect(() => { fetchContacts(); }, [businessId]);

  const fetchContacts = async () => {
    try {
      const res = await API.get(`/auth/contacts?businessId=${businessId}`);
      setContacts(res.data || []);
    } catch (err) {
      setError('Failed to load contacts.');
    }
  };

  const openAdd = () => {
    setEditingContact(null);
    setFormData({ name: '', phone: '', email: '' });
    setShowModal(true);
  };
  const openEdit = (contact) => {
    setEditingContact(contact);
    setFormData({ name: contact.name, phone: contact.phone, email: contact.email || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await API.delete(`/auth/contacts/${id}`);
      setContacts(prev => prev.filter(c => c._id !== id));
    } catch {
      alert('Failed to delete contact');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        const res = await API.put(`/auth/contacts/${editingContact._id}`, formData);
        setContacts(prev => prev.map(c => (c._id === editingContact._id ? res.data.contact : c)));
      } else {
        const res = await API.post(`/auth/contacts`, { ...formData, business_id: businessId });
        setContacts(prev => [...prev, res.data]);
      }
      setShowModal(false);
    } catch {
      alert('Failed to save contact');
    }
  };

  const handleCsvUpload = async () => {
    if (!selectedFile) return alert('Please select a CSV file.');
    const data = new FormData();
    data.append('file', selectedFile);
    data.append('business_id', businessId);
    try {
      const res = await API.post('/auth/contacts/import', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(`Imported ${res.data.count} contacts`);
      setShowCsvModal(false); setSelectedFile(null);
      fetchContacts();
    } catch {
      alert('Failed to upload contacts');
    }
  };

  const filtered = useMemo(() => contacts.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  ), [contacts, search]);

  const [ctPage, setCtPage]         = useState(1);
  const [ctPageSize, setCtPageSize] = useState(10);
  useEffect(() => setCtPage(1), [search]);
  const paginatedContacts = useMemo(() =>
    filtered.slice((ctPage - 1) * ctPageSize, ctPage * ctPageSize),
  [filtered, ctPage, ctPageSize]);

  return (
    <div className="contacts-page">
      {/* Header */}
      <div className="ct-header">
        <h1 className="ct-title">Contacts <span className="ct-count">{contacts.length}</span></h1>
        <div className="ct-header-right">
          <input
            className="ct-search"
            type="text"
            placeholder="Search contacts…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {isAdmin && (
            <div className="ct-actions">
              <button className="ct-btn ct-btn-primary" onClick={openAdd}>+ Add Contact</button>
              <button className="ct-btn ct-btn-lime" onClick={()=>setShowCsvModal(true)}>Import CSV</button>
            </div>
          )}
        </div>
      </div>

      {error && <p style={{color:'red', maxWidth:1100, margin:'0 auto 8px'}}>{error}</p>}

      {/* Table */}
      <div className="ct-card">
        <div className="ct-table-scroll">
        <table className="ct-table">
          <thead>
            <tr>
              <th style={{width:52}}></th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Created At</th>
              {isAdmin && <th style={{width:100}}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="ct-empty">No contacts found</td></tr>
            )}
            {paginatedContacts.map((c) => {
              const initial = (c.name?.[0] || '?').toUpperCase();
              const avatarColor = getAvatarColor(c.name || '');
              return (
                <tr key={c._id}>
                  <td style={{padding:'12px 8px 12px 16px'}}>
                    <div className="ct-avatar" style={{background: avatarColor}}>{initial}</div>
                  </td>
                  <td className="ct-name-cell">{c.name}</td>
                  <td><span className="ct-phone-badge">{c.phone}</span></td>
                  <td>{c.email || <span className="ct-muted">—</span>}</td>
                  <td className="ct-date-cell">{new Date(c.created_at).toLocaleDateString()}</td>
                  {isAdmin && (
                    <td>
                      <div className="ct-row-actions">
                        <button className="ct-icon-btn" onClick={() => openEdit(c)} title="Edit">✎</button>
                        <button className="ct-icon-btn ct-icon-danger" onClick={() => handleDelete(c._id)} title="Delete">🗑</button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>{/* end ct-table-scroll */}
        <Pagination
          page={ctPage}
          totalPages={Math.max(1, Math.ceil(filtered.length / ctPageSize))}
          totalItems={filtered.length}
          pageSize={ctPageSize}
          onPageChange={setCtPage}
          onPageSizeChange={(s) => { setCtPageSize(s); setCtPage(1); }}
        />
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && isAdmin && (
        <div className="ct-modal-overlay" onClick={()=>setShowModal(false)}>
          <div className="ct-modal" onClick={(e)=>e.stopPropagation()}>
            <div className="ct-modal-header">
              <h3 className="ct-modal-title">{editingContact ? 'Edit Contact' : 'Add Contact'}</h3>
              <button className="ct-modal-close" onClick={()=>setShowModal(false)}>×</button>
              
            </div>

            <form onSubmit={handleSave}>
              <label className="ct-label">Name</label>
              <input className="ct-input" name="name" value={formData.name} onChange={e=>setFormData(p=>({...p, name:e.target.value}))} required/>

              <label className="ct-label">Phone</label>
              <input className="ct-input" name="phone" value={formData.phone} onChange={e=>setFormData(p=>({...p, phone:e.target.value}))} required/>

              <label className="ct-label">Email</label>
              <input className="ct-input" name="email" type="email" value={formData.email} onChange={e=>setFormData(p=>({...p, email:e.target.value}))}/>

              <div className="ct-modal-actions">
                <button type="button" className="ct-btn ct-btn-light" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="ct-btn ct-btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* IMPORT CSV MODAL */}
      {showCsvModal && isAdmin && (
        <div className="ct-modal-overlay" onClick={()=>setShowCsvModal(false)}>
          <div className="ct-modal" onClick={(e)=>e.stopPropagation()}>
            <div className="ct-modal-header">
              
              <h3 className="ct-modal-title">Import Contacts via CSV</h3>
              <button className="ct-modal-close" onClick={()=>setShowCsvModal(false)}>×</button>
            </div>

            <div>
              <div className="ct-dropzone" onClick={()=>document.getElementById('ctCsv').click()}>
                Drag & drop your file here or <strong>browse</strong>
              </div>
              <input id="ctCsv" type="file" accept=".csv" style={{display:'none'}}
                     onChange={(e)=> setSelectedFile(e.target.files?.[0] || null)} />
              <div className="ct-file-note">{selectedFile ? `Selected: ${selectedFile.name}` : 'Only .csv files are supported.'}</div>

              <a href={exampleCsv} download style={{display:'block', marginTop:12, fontWeight:800, color:'#1d2752', textDecoration:'underline', textAlign:'center'}}>
                Download Example CSV
              </a>

              <div className="ct-modal-actions">
                <button className="ct-btn ct-btn-light" onClick={()=>{ setShowCsvModal(false); setSelectedFile(null); }}>Cancel</button>
                <button className="ct-btn ct-btn-primary" onClick={handleCsvUpload}>Import</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Contacts;
